import { type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { LOGOS, type LogoName } from './nested/marks'
import { LogoWell, type LogoWellSize } from './nested/well'

/** The icon steps, and nothing of its own: a logo lines up with the glyph it replaces. */
const SIZE_CLASS = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
} as const

type LogoSize = keyof typeof SIZE_CLASS

interface LogoProps extends Omit<ComponentProps<'svg'>, 'children' | 'dangerouslySetInnerHTML' | 'name'> {
  name: LogoName
  /**
   * An icon step, or pixels like Lucide's `size`. Pixels are written as width and height attributes,
   * so a container's CSS can still resize the mark. Omitted, a container that sizes unsized svgs
   * (Button, Cell.Icon, Logo.Well) sizes the mark like the icon it replaces; with no such container
   * it is 16px.
   */
  size?: LogoSize | number
}

/**
 * A third-party brand mark.
 *
 * Not a Lucide icon wrapper. The viewBox is the mark's own, because no two logos here agree on a
 * grid, and nothing sets `stroke`: a logo is filled.
 *
 * `role="img"` with the owner's name rather than `aria-hidden`. An icon beside a label is
 * decoration and the label carries the meaning; a logo is often the only thing naming what a tile
 * connects to, and a tile whose one word is "Connect" is unusable without it.
 */
function Logo({ name, size, className, ...props }: LogoProps) {
  const mark = LOGOS[name]
  const pixels = typeof size === 'number' ? size : undefined
  const step = typeof size === 'string' ? size : undefined

  return (
    <svg
      viewBox={mark.viewBox}
      role="img"
      aria-label={mark.name}
      // The colour for any path without a fill of its own. Without it SVG falls back to black, which
      // vanishes on a dark ground.
      fill="currentColor"
      // The default is `h-4 w-4`, not `size-4`: containers size any svg whose class has no `size-`,
      // and this has to stay one of those for them to reach it.
      width={pixels}
      height={pixels}
      className={twMerge('shrink-0', step ? SIZE_CLASS[step] : pixels === undefined && 'h-4 w-4', className)}
      // Trusted: vendored SVG checked into nested/marks/.
      dangerouslySetInnerHTML={{ __html: mark.body }}
      {...props}
    />
  )
}

Logo.Well = LogoWell

const logoNames = Object.keys(LOGOS) as Array<LogoName>

export { Logo, LOGOS, logoNames, type LogoName, type LogoSize, type LogoWellSize }
